import DuoSignUpLayout from '@/components/v2/DuoSignUpLayout'
import DuoTextField from '@/components/v2/DuoTextField'
import { setPasswordInSessionStorage } from '@app-studio-web/ui/helpers/sessionStorage'
import { validatePassword } from '@app-studio-web/ui/helpers/validations'
import type { IsUserSignedInLoaderData } from '@app-studio-web/ui/loaders/isUserSignedInLoader'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLoaderData, useNavigate } from 'react-router-dom'

/**
 * The password half of Duolingo's "Create your profile" form. Behaviour is the
 * shared `PasswordPage`'s, unchanged: already-signed-in users skip to
 * /sign-up/preparing, a valid password is stashed in session storage and the
 * step hands off to /sign-up/email.
 */
export default function PasswordPage() {
  const [password, setPassword] = useState('')
  const [passwordValid, setPasswordValid] = useState(true)

  const { isUserSignedIn } = useLoaderData() as IsUserSignedInLoaderData
  const navigate = useNavigate()
  const { t } = useTranslation()

  if (isUserSignedIn) {
    return <Navigate to="/sign-up/preparing" />
  }

  const isPasswordValid = validatePassword(password)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isPasswordValid) {
      setPasswordInSessionStorage(password)
      navigate('/sign-up/email')
    } else {
      setPasswordValid(false)
    }
  }

  return (
    <DuoSignUpLayout
      fineprint={
        <p
          className={
            passwordValid
              ? 'text-duo-muted text-center text-xs leading-relaxed'
              : 'text-duo-red text-center text-xs leading-relaxed'
          }
        >
          {t('pages.signUp.password.helperText')}
        </p>
      }
    >
      <form className="space-y-4" noValidate onSubmit={handleSubmit}>
        <DuoTextField
          autoComplete="new-password"
          invalid={!passwordValid}
          name="password"
          onChange={(event) => setPassword(event.target.value)}
          placeholder={t('shared.password')}
          type="password"
          value={password}
          withReveal
        />

        <button
          className="duo-button-primary"
          data-testid="button-continue-credentials"
          disabled={password.length < 1}
          type="submit"
        >
          {t('shared.continue')}
        </button>
      </form>
    </DuoSignUpLayout>
  )
}
